import React from 'react';
import { Reorder, useDragControls } from 'motion/react';
import { GripVertical, Trash2 } from 'lucide-react';
import { AddRowButton, IconButton } from '../editor/fields';
import { CaseStudyStat, generateId } from './types';

// Headline number tiles: a big value ("48h") and a small label, in a
// responsive grid. Same drag-to-reorder pattern as the blog's block cards.

interface Props {
  stats: CaseStudyStat[];
  onChange: (stats: CaseStudyStat[]) => void;
}

export const StatsSection: React.FC<Props> = ({ stats, onChange }) => {
  const update = (id: string, patch: Partial<CaseStudyStat>) => onChange(stats.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const remove = (id: string) => onChange(stats.filter((s) => s.id !== id));
  const add = () => onChange([...stats, { id: generateId(), value: '', label: '', sortOrder: stats.length }]);

  return (
    <div>
      {stats.length ? (
        <Reorder.Group as="div" axis="y" values={stats} onReorder={onChange} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stats.map((stat) => (
            <StatTile key={stat.id} stat={stat} onChange={(patch) => update(stat.id, patch)} onDelete={() => remove(stat.id)} />
          ))}
        </Reorder.Group>
      ) : (
        <p className="text-[12.5px] text-[color:var(--oa-muted)]">No stats yet — add a few headline numbers.</p>
      )}
      <div className="mt-3">
        <AddRowButton onClick={add}>Add stat</AddRowButton>
      </div>
    </div>
  );
};

const StatTile: React.FC<{ stat: CaseStudyStat; onChange: (patch: Partial<CaseStudyStat>) => void; onDelete: () => void }> = ({ stat, onChange, onDelete }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item as="div" value={stat} dragListener={false} dragControls={controls} className="group/tile relative rounded-lg border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-3 pb-3 pt-2">
      <div className="mb-1 flex items-center justify-between">
        <button
          type="button"
          onPointerDown={(e) => controls.start(e)}
          className="grid h-5 w-5 cursor-grab touch-none place-items-center rounded text-[color:var(--oa-faint)] hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-muted)] active:cursor-grabbing"
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <IconButton tone="danger" onClick={onDelete} aria-label="Remove stat" className="opacity-0 transition-opacity group-hover/tile:opacity-100">
          <Trash2 className="h-3.5 w-3.5" />
        </IconButton>
      </div>
      <input
        value={stat.value}
        onChange={(e) => onChange({ value: e.target.value })}
        placeholder="48h"
        className="w-full bg-transparent font-montserrat text-[24px] font-extrabold text-[color:var(--oa-text)] outline-none placeholder:text-[color:var(--oa-faint)]"
      />
      <input
        value={stat.label}
        onChange={(e) => onChange({ label: e.target.value })}
        placeholder="Turnaround"
        className="mt-0.5 w-full bg-transparent text-[12px] text-[color:var(--oa-muted)] outline-none placeholder:text-[color:var(--oa-faint)]"
      />
    </Reorder.Item>
  );
};
